import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { errMessage } from "../lib/errors";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Shield, Users, CalendarCheck, Truck, Utensils,
  DollarSign, BookOpen, Settings as SettingsIcon, Download, Upload,
  Send, ShieldCheck, RotateCcw, CheckCircle2, XCircle, QrCode, TrendingUp,
  Wallet, Mail, Rocket, Instagram, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Sparkles,
  Printer, Lock, FileDown, Eye, Award, Gift, Check
} from "lucide-react";
import { MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { menuHighlights } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import TableCodesPanel from "../components/admin/TableCodesPanel";
import AnalyticsPanel from "../components/admin/AnalyticsPanel";
import PayoutsPanel from "../components/admin/PayoutsPanel";
import GoLivePanel from "../components/admin/GoLivePanel";
import InstagramPanel from "../components/admin/InstagramPanel";
import ScheduledSpecialsPanel from "../components/admin/ScheduledSpecialsPanel";
import LoyaltyStampQrPanel from "../components/admin/LoyaltyStampQrPanel";
import PushBlastPanel from "../components/admin/PushBlastPanel";
import LeadsPanel from "../components/admin/LeadsPanel";
import SmsLogPanel from "../components/admin/SmsLogPanel";
import AiStudioPanel from "../components/admin/AiStudioPanel";
import DishImageField from "../components/admin/DishImageField";
import PrintablesPanel from "../components/admin/PrintablesPanel";
import SpecialSlipPanel from "../components/admin/SpecialSlipPanel";
import ReferralsPanel from "../components/admin/ReferralsPanel";
import ReservationDecisionModal from "../components/admin/ReservationDecisionModal";
import InstallOfficeAppButton from "../components/InstallOfficeAppButton";
import OfficeAlertsButton from "../components/OfficeAlertsButton";
import OfficeAlertsNudge from "../components/OfficeAlertsNudge";
import { useOfficeManifest } from "../hooks/useOfficeManifest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { key: "orders",       label: "Orders",       icon: DollarSign },
  { key: "reservations", label: "Reservations", icon: CalendarCheck },
  { key: "catering",     label: "Catering",     icon: Utensils },
  { key: "delivery",     label: "Delivery",     icon: Truck },
  { key: "users",        label: "Users",        icon: Users },
  { key: "menu",         label: "Menu",         icon: BookOpen },
  { key: "tables",       label: "Tables",       icon: QrCode },
  { key: "loyalty-qr",   label: "Stamp QR",     icon: Award },
  { key: "push-blast",   label: "Push Blast",   icon: Send },
  { key: "analytics",    label: "Analytics",    icon: TrendingUp },
  { key: "payouts",      label: "Payouts",      icon: Wallet },
  { key: "digest",       label: "Digest",       icon: Mail },
  { key: "sms-log",      label: "SMS Log",      icon: MessageSquare },
  { key: "ai",           label: "AI Studio",    icon: Sparkles },
  { key: "instagram",    label: "Instagram",    icon: Instagram },
  { key: "leads",        label: "Leads",        icon: Mail },
  { key: "referrals",    label: "Referrals",    icon: Gift },
  { key: "printables",   label: "Printables",   icon: FileDown },
  { key: "golive",       label: "Go-Live",      icon: Rocket },
  { key: "pos",          label: "POS Bridge",   icon: Send },
  { key: "exports",      label: "Exports",      icon: Download },
  { key: "settings",     label: "Settings",     icon: SettingsIcon }
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Money({ cents, currency = "aud" }) {
  const n = (cents || 0) / 100;
  return <>${n.toFixed(2)} {currency.toUpperCase()}</>;
}

export default function Admin() {
  const { isAuthenticated, isAdmin, loading, authHeaders } = useAuth();
  // Deep-link support: /admin?tab=orders lets the installed PWA app shortcuts
  // (Orders / Kitchen / Referrals) drop the user straight into a specific tab.
  const [tab, setTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("tab");
      const known = new Set([
        "orders","reservations","catering","delivery","users","menu","tables",
        "loyalty-qr","push-blast","analytics","payouts","digest","instagram",
        "leads","referrals","printables","golive","pos","exports","settings","sms-log","ai",
      ]);
      return known.has(p) ? p : "orders";
    } catch { return "orders"; }
  });
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  // Reservation Confirm/Decline modal state
  const [decisionModal, setDecisionModal] = useState(null); // {kind, reservation}

  // Swap the browser's PWA manifest so this page's "Add to Home Screen"
  // installs "Mamamina Office" (dark theme + dedicated icon), not the
  // customer-facing "Mamamina's Soulfeed" experience.
  useOfficeManifest();

  const dataTabs = useMemo(
    () => new Set(["orders", "reservations", "catering", "delivery", "users"]),
    []
  );

  const fetchData = useCallback(async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const s = await axios.get(`${API}/admin/summary`, { headers: authHeaders });
      setSummary(s.data);
      if (dataTabs.has(tab)) {
        const list = await axios.get(`${API}/admin/${tab}`, { headers: authHeaders });
        setItems(list.data.items || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  }, [isAdmin, authHeaders, tab, dataTabs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="mm-dark min-h-screen bg-[var(--mm-bg)] flex items-center justify-center">
        <Loader2 size={32} className="text-[var(--mm-accent)] animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login?next=/admin" replace />;
  if (!isAdmin) return <Navigate to="/account" replace />;

  const resendPos = async (session_id) => {
    try {
      const { data } = await axios.post(`${API}/admin/orders/${session_id}/resend-pos`, {}, { headers: authHeaders });
      toast({ title: data.ok ? "Ticket resent to POS" : "Send failed", description: data.ok ? "Check TapTouch inbox." : "Check settings or Resend key." });
    } catch (e) {
      toast({ title: "Resend failed", description: errMessage(e) });
    }
  };

  const setOrderStatus = async (session_id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${session_id}/status`, { status }, { headers: authHeaders });
      let msg;
      if (data.sms_sent && data.eta_minutes) {
        msg = `Customer notified — ETA ~${data.eta_minutes} mins`;
      } else if (data.sms_sent) {
        msg = "Customer notified by SMS.";
      } else {
        msg = "Status saved.";
      }
      toast({ title: `Marked ${status.replace("_", " ")}`, description: msg });
      // Refresh order list so the pill updates
      const list = await axios.get(`${API}/admin/orders`, { headers: authHeaders });
      setItems(list.data.items || []);
    } catch (e) {
      toast({ title: "Update failed", description: errMessage(e) });
    }
  };

  const setReservationStatus = async (res_id, payload) => {
    // payload = {status, table_number?, alternative_times?, note?}
    try {
      const body = typeof payload === "string" ? { status: payload } : payload;
      const { data } = await axios.patch(
        `${API}/admin/reservations/${res_id}/status`,
        body,
        { headers: authHeaders },
      );
      const bits = [];
      if (data.sms_sent) bits.push("SMS sent");
      if (data.email_sent) bits.push("email sent");
      const msg = bits.length ? `Customer notified — ${bits.join(" + ")}.` : "Status saved.";
      toast({ title: `Reservation ${body.status}`, description: msg });
      const list = await axios.get(`${API}/admin/reservations`, { headers: authHeaders });
      setItems(list.data.items || []);
    } catch (e) {
      toast({ title: "Update failed", description: errMessage(e) });
    }
  };

  const openDecisionModal = (kind, reservation) => {
    setDecisionModal({ kind, reservation });
  };

  const toggleStaff = async (userRow) => {
    if (userRow.is_admin) {
      toast({ title: "Not allowed", description: "You can't change staff flag on an admin account." });
      return;
    }
    const next = !userRow.is_staff;
    try {
      await axios.patch(`${API}/admin/users/${userRow.id}/staff`, { is_staff: next }, { headers: authHeaders });
      toast({
        title: next ? "Promoted to staff" : "Staff access removed",
        description: next
          ? `${userRow.email} can now sign in to the back office at /staff.`
          : `${userRow.email} is back to a regular customer account.`,
      });
      const list = await axios.get(`${API}/admin/users`, { headers: authHeaders });
      setItems(list.data.items || []);
    } catch (e) {
      toast({ title: "Update failed", description: errMessage(e) });
    }
  };

  const resetPin = async (userRow) => {
    if (!userRow.pin_set && !userRow.pin_locked) {
      toast({ title: "No PIN to reset", description: `${userRow.email} doesn't have a PIN set.` });
      return;
    }
    const verb = userRow.pin_locked ? "unlock" : "clear the PIN for";
    if (!window.confirm(`Are you sure you want to ${verb} ${userRow.email}? They'll need to set a new PIN from the /staff dashboard.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userRow.id}/pin`, { headers: authHeaders });
      toast({ title: "PIN cleared", description: `${userRow.email} can now sign in with password and set a new PIN.` });
      const list = await axios.get(`${API}/admin/users`, { headers: authHeaders });
      setItems(list.data.items || []);
    } catch (e) {
      toast({ title: "Reset failed", description: errMessage(e) });
    }
  };

  return (
    <div className="mm-dark min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-14">
        <Link to="/account" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-10">
          <ArrowLeft size={14} /> Account
        </Link>

        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-[var(--mm-accent)]" />
            <div className="section-label">Admin Dashboard</div>
          </div>
          <div className="flex items-center gap-3">
            <OfficeAlertsButton />
            <InstallOfficeAppButton />
          </div>
        </div>
        <h1 className="font-display italic text-5xl md:text-6xl text-[var(--mm-cream)] leading-tight mb-8">
          Mama&apos;s <span className="text-[var(--mm-accent)]">control room.</span>
        </h1>

        <OfficeAlertsNudge />

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatCard label="Paid Orders" value={summary.paid_orders} sub={`${summary.orders} total`} />
            <StatCard label="Revenue" value={<Money cents={summary.revenue_cents} />} sub="paid orders" />
            <StatCard label="New Reservations" value={summary.new_reservations} sub={`${summary.reservations} total`} />
            <StatCard label="New Enquiries" value={summary.new_catering + summary.new_delivery} sub={`${summary.catering + summary.delivery} total`} />
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[var(--mm-line)] pb-4">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-[0.72rem] tracking-[0.22em] uppercase border transition-all ${
                  tab === t.key
                    ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                    : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
                }`}
              >
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "orders" && <OrdersHealthStrip authHeaders={authHeaders} />}
        {tab === "menu" && <MenuManager authHeaders={authHeaders} toast={toast} />}
        {tab === "tables" && <TableCodesPanel />}
        {tab === "loyalty-qr" && <LoyaltyStampQrPanel />}
        {tab === "push-blast" && <PushBlastPanel />}
        {tab === "analytics" && <AnalyticsPanel authHeaders={authHeaders} />}
        {tab === "payouts" && <PayoutsPanel authHeaders={authHeaders} />}
        {tab === "digest" && <DigestPanel authHeaders={authHeaders} toast={toast} />}
        {tab === "instagram" && <InstagramPanel authHeaders={authHeaders} toast={toast} />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "sms-log" && <SmsLogPanel authHeaders={authHeaders} />}
        {tab === "ai" && <AiStudioPanel toast={toast} />}
        {tab === "referrals" && <ReferralsPanel />}
        {tab === "printables" && <PrintablesPanel />}
        {tab === "golive" && <GoLivePanel authHeaders={authHeaders} />}
        {tab === "settings" && <SettingsPanel authHeaders={authHeaders} toast={toast} />}
        {tab === "exports" && <ExportsPanel authHeaders={authHeaders} />}
        {tab === "pos" && <PosLog authHeaders={authHeaders} />}

        {dataTabs.has(tab) && (
          busy ? (
            <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-[var(--mm-cream-muted)] font-light">No entries yet.</div>
          ) : (
            <DataTable tab={tab} items={items} onResendPos={resendPos} onSetStatus={setOrderStatus} onOpenDecision={openDecisionModal} onToggleStaff={toggleStaff} onResetPin={resetPin} />
          )
        )}
      </div>

      {/* Reservation Confirm/Decline modal */}
      <ReservationDecisionModal
        open={!!decisionModal}
        kind={decisionModal?.kind}
        reservation={decisionModal?.reservation}
        onClose={() => setDecisionModal(null)}
        onSubmit={(payload) => setReservationStatus(decisionModal.reservation.id, payload)}
      />
    </div>
  );
}

/* ----------- Weekly Digest Preview & Send ----------- */
function DigestPanel({ authHeaders, toast }) {
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const previewUrlRef = useRef(null);

  React.useEffect(() => {
    // fetch preview HTML, wrap in blob URL for iframe
    axios.get(`${API}/admin/digest/preview`, { headers: authHeaders, responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => setPreviewUrl(null));
    return () => {
      // Use ref (not the closed-over `previewUrl`) to always revoke the latest.
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    };
  }, [authHeaders]);

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/digest/send-now`, {}, { headers: authHeaders });
      toast({ title: data.ok ? "Digest sent" : "Send failed", description: data.ok ? "Check the admin inbox." : "Check Resend key + POS email setting." });
    } catch (e) {
      toast({ title: "Send failed", description: errMessage(e) });
    } finally { setBusy(false); }
  };

  const [salesBusy, setSalesBusy] = React.useState(false);
  const sendSalesText = async () => {
    setSalesBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/daily-sales/send`, {}, { headers: authHeaders });
      toast({
        title: data.sent ? "Sales text sent" : "Composed but not delivered",
        description: data.message || data.skipped || "Check the SMS Log tab for details.",
      });
    } catch (e) {
      toast({ title: "Send failed", description: errMessage(e) });
    } finally { setSalesBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2">Weekly Digest Email</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Delivered every Sunday at 20:00 Brisbane time to the POS email in Settings. Includes revenue vs last week, top dishes, combos, cook-speed movers, and upcoming reservations. Preview below.
          </p>
        </div>
        <button
          onClick={sendNow}
          disabled={busy}
          data-testid="digest-send-now"
          className="btn-primary inline-flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now
        </button>
      </div>

      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2">Daily Sales Text</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Every night at 10pm Brisbane time, Mama's mobile gets a one-line snapshot: today's paid orders,
            revenue and the best seller. Toggle it in Settings. Tap to send today's snapshot right now.
          </p>
        </div>
        <button
          onClick={sendSalesText}
          disabled={salesBusy}
          data-testid="daily-sales-send-now"
          className="btn-primary inline-flex items-center gap-2"
        >
          {salesBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Text it now
        </button>
      </div>

      {previewUrl ? (
        <iframe
          src={previewUrl}
          title="Weekly Digest Preview"
          className="w-full min-h-[900px] bg-white border border-[var(--mm-line)]"
          data-testid="digest-preview-iframe"
        />
      ) : (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
      <div className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2">{label}</div>
      <div className="font-display text-3xl text-[var(--mm-accent)]">{value}</div>
      {sub && <div className="text-xs text-[var(--mm-cream-muted)] mt-1">{sub}</div>}
    </div>
  );
}

/* ----------- Category Reorder (drag-and-drop) ----------- */
function MenuPdfPanel({ authHeaders, toast }) {
  const pdfUrl = `${API}/menu.pdf`;
  const [tentCount, setTentCount] = useState(12);
  const tentUrl = `${API}/table-tents.pdf?count=${tentCount}`;
  return (
    <div className="space-y-3">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4" data-testid="menu-pdf-panel">
        <div className="flex-1">
          <div className="section-label mb-2 flex items-center gap-2">
            <FileDown size={13} /> Printable Menu PDF
          </div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Print-ready double-sided A4 — front: every dish with photos & prices in two tidy columns; back: drinks, family bundles, catering, hours & QR. Attach it to Google Business, share on WhatsApp, or print for the counter.
          </p>
          <p className="text-xs text-[var(--mm-cream-muted)] opacity-70 mt-1">
            Regenerated from the live menu each time — always current.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="menu-pdf-preview"
             className="btn-outline inline-flex items-center gap-2 text-xs">
            <Eye size={13} /> Preview
          </a>
          <a href={pdfUrl} download="Mamamina-Soulfeed-Menu.pdf" data-testid="menu-pdf-download"
             className="btn-primary inline-flex items-center gap-2">
            <FileDown size={14} /> Download PDF
          </a>
        </div>
      </div>

      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4" data-testid="tents-pdf-panel">
        <div className="flex-1">
          <div className="section-label mb-2 flex items-center gap-2">
            <FileDown size={13} /> QR Table Tents
          </div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            One A4 sheet per table — logo, table number, QR code linking straight to the ordering site with the table pre-selected. Fold along the dashed line and the tent stands up with both sides visible.
          </p>
          <p className="text-xs text-[var(--mm-cream-muted)] opacity-70 mt-1">
            Each QR pre-selects the table so kitchen tickets show <span className="text-[var(--mm-accent)]">T#5</span> automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-[var(--mm-cream-muted)]">
            <label htmlFor="tent-count" className="whitespace-nowrap">Tables:</label>
            <input
              id="tent-count"
              data-testid="tent-count"
              type="number"
              min={1}
              max={100}
              value={tentCount}
              onChange={(e) => setTentCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-16 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-center focus:border-[var(--mm-accent)] outline-none"
            />
          </div>
          <a href={tentUrl} target="_blank" rel="noreferrer" data-testid="tents-preview"
             className="btn-outline inline-flex items-center gap-2 text-xs">
            <Eye size={13} /> Preview
          </a>
          <a href={tentUrl} download={`Mamamina-Table-Tents-${tentCount}.pdf`} data-testid="tents-download"
             className="btn-primary inline-flex items-center gap-2">
            <FileDown size={14} /> Download PDF
          </a>
        </div>
      </div>

      <SpecialSlipPanel authHeaders={authHeaders} toast={toast} />
    </div>
  );
}



function CategoryReorder({ authHeaders, toast }) {
  const [order, setOrder] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    axios.get(`${API}/menu/categories`).then(({ data }) => {
      setOrder(data.order || []);
      setAllowed(data.allowed || []);
    }).catch(() => {});
  }, []);

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= order.length || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOrder(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/menu/categories`, { order }, { headers: authHeaders });
      setOrder(data.order || order);
      setDirty(false);
      toast({ title: "Order saved", description: `${(data.order || []).join(" › ")}` });
    } catch (e) {
      toast({ title: "Save failed", description: errMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setOrder([...allowed]);
    setDirty(true);
  };

  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="menu-category-reorder">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2 flex items-center gap-2"><GripVertical size={14} /> Category Order</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Drag categories to reorder how they appear on the home menu grid.
            Whatever&apos;s on top leads the storefront. &ldquo;All&rdquo; always stays first.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={reset}
            disabled={saving}
            className="btn-outline text-xs"
            data-testid="cat-reset-btn"
          >
            Reset to default
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cat-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {saving ? "Saving..." : dirty ? "Save order" : "Saved"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="cat-reorder-list">
        {order.map((cat, i) => (
          <div
            key={cat}
            draggable
            onDragStart={(e) => { setDragging(i); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); e.dataTransfer.dropEffect = "move"; }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (dragging !== null && dragging !== i) move(dragging, i);
              setDragging(null);
              setDragOver(null);
            }}
            onDragEnd={() => { setDragging(null); setDragOver(null); }}
            data-testid={`cat-item-${cat}`}
            className={`
              flex items-center gap-2 p-3 border cursor-move select-none
              transition-all duration-150
              ${dragging === i ? "opacity-40 border-[var(--mm-accent)]" : "border-[var(--mm-line)]"}
              ${dragOver === i && dragging !== i ? "border-[var(--mm-accent)] bg-[var(--mm-accent)]/10" : "bg-[var(--mm-bg)]"}
              hover:border-[var(--mm-accent)]
            `}
          >
            <GripVertical size={14} className="text-[var(--mm-cream-muted)] shrink-0" />
            <span className="text-[0.7rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] w-5 shrink-0">{i + 1}</span>
            <span className="flex-1 text-[var(--mm-cream)] font-light">{cat}</span>
            <div className="flex items-center gap-0.5 opacity-70">
              <button
                onClick={() => move(i, i - 1)}
                disabled={i === 0}
                className="w-6 h-6 flex items-center justify-center border border-[var(--mm-line)] hover:border-[var(--mm-accent)] disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`cat-up-${cat}`}
                aria-label={`Move ${cat} up`}
              >
                <ArrowUp size={11} />
              </button>
              <button
                onClick={() => move(i, i + 1)}
                disabled={i === order.length - 1}
                className="w-6 h-6 flex items-center justify-center border border-[var(--mm-line)] hover:border-[var(--mm-accent)] disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid={`cat-down-${cat}`}
                aria-label={`Move ${cat} down`}
              >
                <ArrowDown size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {dirty && (
        <div className="mt-3 text-[0.65rem] tracking-[0.2em] uppercase text-[var(--mm-accent)]" data-testid="cat-dirty-hint">
          Unsaved changes — click &ldquo;Save order&rdquo; to apply
        </div>
      )}
    </div>
  );
}


/* ----------- Menu Manager ----------- */
function MenuManager({ authHeaders, toast }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [newForm, setNewForm] = useState({ name: "", tag: "Signature", price: "", description: "", image: "" });
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/menu`, { headers: authHeaders });
      const overrideMap = {};
      (data.items || []).forEach((o) => { overrideMap[o.id] = o; });
      const staticIds = new Set(menuHighlights.map((m) => m.id));
      const merged = menuHighlights.map((m) => {
        const o = overrideMap[m.id] || {};
        return {
          id: m.id,
          name: o.name || m.name,
          tag: o.tag || m.tag,
          description: o.description != null ? o.description : (m.description || ""),
          image: o.image != null ? o.image : (m.image || ""),
          lookup_key: o.lookup_key || `mamamina_${m.id}`,
          basePrice: m.price,
          price: o.price != null ? o.price : Number((m.price || "").replace(/[^\d.]/g, "")) || 0,
          sold_out: !!o.sold_out,
          stock: o.stock,
          popular: o.popular,
          is_special: !!o.is_special,
          custom: false,
        };
      });
      // DB-only items (added via "Add new dish")
      const extras = (data.items || [])
        .filter((o) => !staticIds.has(o.id) && o.name)
        .map((o) => ({
          id: o.id,
          name: o.name,
          tag: o.tag || "Signature",
          description: o.description || "",
          image: o.image || "",
          lookup_key: o.lookup_key,
          basePrice: "",
          price: o.price || 0,
          sold_out: !!o.sold_out,
          stock: o.stock,
          popular: !!o.popular,
          is_special: !!o.is_special,
          custom: true,
        }));
      setItems([...merged, ...extras]);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (item, patch) => {
    const updated = { ...item, ...patch };
    // Client-side single-featured invariant for Today's Special (backend also
    // enforces this — belt & braces so the UI matches immediately).
    setItems((arr) => arr.map((x) => {
      if (x.id === item.id) return updated;
      if (patch.is_special === true) return { ...x, is_special: false };
      return x;
    }));
    try {
      await axios.put(`${API}/admin/menu/${item.id}`, {
        id: item.id,
        lookup_key: updated.lookup_key,
        name: updated.name,
        tag: updated.tag,
        description: updated.description,
        image: updated.image,
        price: Number(updated.price),
        sold_out: updated.sold_out,
        stock: updated.stock === "" ? null : (updated.stock != null ? Number(updated.stock) : null),
        popular: updated.popular,
        is_special: updated.is_special,
      }, { headers: authHeaders });
    } catch (e) {
      toast({ title: "Save failed", description: errMessage(e) });
      load();
    }
  };

  const createNew = async () => {
    if (!newForm.name.trim()) {
      toast({ title: "Name required" });
      return;
    }
    try {
      await axios.post(`${API}/admin/menu`, {
        name: newForm.name.trim(),
        tag: newForm.tag,
        price: Number(newForm.price) || 0,
        description: newForm.description.trim() || null,
        image: newForm.image.trim() || null,
        popular: false,
      }, { headers: authHeaders });
      toast({ title: "Dish added", description: "Now live on the menu." });
      setNewForm({ name: "", tag: "Signature", price: "", description: "", image: "" });
      setShowNew(false);
      load();
    } catch (e) {
      toast({ title: "Add failed", description: errMessage(e) });
    }
  };

  const remove = async (item) => {
    if (!item.custom) {
      toast({ title: "Cannot delete a core dish", description: "Toggle Sold Out instead — core dishes live in the codebase and require a code change to remove permanently." });
      return;
    }
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/menu/${item.id}`, { headers: authHeaders });
      toast({ title: "Deleted", description: `${item.name} removed.` });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: errMessage(e) });
    }
  };

  const doImport = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/menu/import`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      toast({ title: "Import complete", description: `Updated ${data.updated}, created ${data.created}` });
      setFile(null);
      load();
    } catch (e) {
      toast({ title: "Import failed", description: errMessage(e) });
    }
  };

  const downloadExport = () => {
    window.open(`${API}/admin/menu/export.csv`, "_blank");
  };

  const tagOptions = ["Signature", "Grill", "Wraps", "West African", "Sides", "Liberian", "Comfort"];

  return (
    <div className="space-y-6">
      <MenuPdfPanel authHeaders={authHeaders} toast={toast} />
      <CategoryReorder authHeaders={authHeaders} toast={toast} />
      <ScheduledSpecialsPanel authHeaders={authHeaders} toast={toast} />

      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2">Menu Management</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Edit name / description / image / price / tag inline. Add new dishes with the button on the right. TapTouch CSV round-trip still supported.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowNew((v) => !v)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="menu-add-new-btn"
          >
            <Plus size={14} /> {showNew ? "Cancel" : "Add new dish"}
          </button>
          <label className="btn-outline cursor-pointer" data-testid="menu-import-btn">
            <Upload size={14} className="inline mr-2" />
            {file ? file.name : "Choose CSV"}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {file && (
            <button onClick={doImport} className="btn-primary" data-testid="menu-import-upload">
              Upload & Sync
            </button>
          )}
          <button onClick={downloadExport} className="btn-outline" data-testid="menu-export-download">
            <Download size={14} className="inline mr-2" /> Export CSV
          </button>
        </div>
      </div>

      {showNew && (
        <div className="p-5 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)] space-y-3" data-testid="menu-new-form">
          <div className="section-label mb-2">New Dish</div>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              type="text"
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="Dish name (required)"
              data-testid="menu-new-name"
              className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
            />
            <select
              value={newForm.tag}
              onChange={(e) => setNewForm({ ...newForm, tag: e.target.value })}
              className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
            >
              {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              type="number"
              value={newForm.price}
              onChange={(e) => setNewForm({ ...newForm, price: e.target.value })}
              placeholder="Price AUD (e.g. 20)"
              data-testid="menu-new-price"
              className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
            />
            <DishImageField
              value={newForm.image}
              onChange={(url) => setNewForm((f) => ({ ...f, image: url }))}
              testId="menu-new-image"
              toast={toast}
              variant="form"
            />
            <textarea
              value={newForm.description}
              onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
              placeholder="Description"
              rows={2}
              className="md:col-span-2 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none resize-none"
            />
          </div>
          <div className="flex justify-end">
            <button onClick={createNew} className="btn-primary inline-flex items-center gap-2" data-testid="menu-new-submit">
              <Plus size={14} /> Create dish
            </button>
          </div>
        </div>
      )}

      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]">
                <th className="py-3 pr-4">ID</th>
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Tag</th>
                <th className="py-3 pr-4">Price</th>
                <th className="py-3 pr-4">Image URL</th>
                <th className="py-3 pr-4">Description</th>
                <th className="py-3 pr-4">Sold Out</th>
                <th className="py-3 pr-4">Special</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className={`border-b border-[var(--mm-line)]/60 text-[var(--mm-cream)] ${it.custom ? "bg-[var(--mm-accent)]/5" : ""}`}>
                  <td className="py-3 pr-4">
                    {it.id}
                    {it.custom && <span className="ml-1 text-[0.55rem] tracking-[0.2em] uppercase text-[var(--mm-accent)]">new</span>}
                  </td>
                  <td className="py-3 pr-4">
                    <input
                      defaultValue={it.name}
                      onBlur={(e) => { if (e.target.value !== it.name) save(it, { name: e.target.value }); }}
                      className="w-40 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-sm focus:border-[var(--mm-accent)] outline-none"
                      data-testid={`menu-name-${it.id}`}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <select
                      defaultValue={it.tag}
                      onChange={(e) => { if (e.target.value !== it.tag) save(it, { tag: e.target.value }); }}
                      className="bg-[var(--mm-bg)] border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none"
                    >
                      {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td className="py-3 pr-4">
                    <input
                      type="number"
                      defaultValue={it.price}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== Number(it.price)) save(it, { price: v });
                      }}
                      className="w-20 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-sm focus:border-[var(--mm-accent)] outline-none"
                      data-testid={`menu-price-${it.id}`}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <DishImageField
                      value={it.image || ""}
                      onChange={(url) => save(it, { image: url })}
                      testId={`menu-image-${it.id}`}
                      toast={toast}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <textarea
                      defaultValue={it.description || ""}
                      onBlur={(e) => { if (e.target.value !== (it.description || "")) save(it, { description: e.target.value }); }}
                      rows={2}
                      className="w-64 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-2 py-1 text-xs focus:border-[var(--mm-accent)] outline-none resize-none"
                      data-testid={`menu-desc-${it.id}`}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!it.sold_out}
                        onChange={(e) => save(it, { sold_out: e.target.checked })}
                        className="sr-only peer"
                        data-testid={`menu-soldout-${it.id}`}
                      />
                      <span className={`w-10 h-5 rounded-full transition-colors relative ${it.sold_out ? "bg-[var(--mm-accent)]" : "bg-[var(--mm-line)]"}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${it.sold_out ? "translate-x-5" : ""}`} />
                      </span>
                    </label>
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      onClick={() => save(it, { is_special: !it.is_special })}
                      title={it.is_special ? "Clear Today's Special" : "Feature as Today's Special"}
                      data-testid={`menu-special-${it.id}`}
                      className={`
                        w-8 h-8 rounded-full inline-flex items-center justify-center transition-all
                        ${it.is_special ? "bg-[var(--mm-accent)] text-black" : "border border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] hover:border-[var(--mm-accent)]"}
                      `}
                    >
                      <Sparkles size={13} className={it.is_special ? "fill-black" : ""} />
                    </button>
                  </td>
                  <td className="py-3 pr-4">
                    {it.custom && (
                      <button
                        onClick={() => remove(it)}
                        className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 inline-flex items-center gap-1"
                        data-testid={`menu-delete-${it.id}`}
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------- Settings Panel ----------- */
function SettingsPanel({ authHeaders, toast }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
      setRows(data.items || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (key, value) => {
    try {
      await axios.put(`${API}/admin/settings`, { key, value }, { headers: authHeaders });
      toast({ title: "Saved", description: `${key} updated` });
    } catch (e) {
      toast({ title: "Save failed", description: errMessage(e) });
    }
  };

  const LABELS = {
    pos_email: "TapTouch POS Email (kitchen inbox)",
    owner_alert_email: "Owner Alert Email (new-order emails — blank = admin login email)",
    order_email_alerts_enabled: "Order Email Alerts Enabled (true / false — email the owner on every paid order)",
    special_slip_email_enabled: "Special Slip Email Enabled (true / false — email the print-ready Today's Special slips at 7am when the special changes)",
    driver_mobile: "Driver Mobile (delivery dispatch SMS)",
    admin_mobile: "Admin Mobile (order/reservation alerts)",
    sender_email: "Sender Email (from address on POS tickets)",
    pos_enabled: "POS Email Tickets Enabled (true / false)",
    driver_sms_enabled: "Driver Dispatch SMS Enabled (true / false)",
    preparing_sms_enabled: "Preparing SMS Enabled (true / false — 'we've started your order')",
    ready_sms_enabled: "Ready SMS Enabled (true / false — 'your order is ready')",
    base_prep_minutes: "Base Prep Time (minutes) — first item ETA",
    per_item_prep_minutes: "Per-Item Prep Time (minutes) — add per extra item",
    print_webhook_enabled: "Thermal Printer Webhook Enabled (true / false)",
    print_webhook_url: "Thermal Printer Webhook URL (POST text/plain — PrintNode/StarCloudPRNT/etc)",
    open_of_day_print_enabled: "Auto-print Shift Notes at open of day (true / false)",
    open_of_day_print_hour: "Open-of-day print hour (Brisbane, 0-23)",
    review_sms_enabled: "Review-ask SMS Enabled (true / false — texts a Google review link when an order is completed)",
    google_review_url: "Google Review Link (sent in the review-ask SMS)",
    birthday_sms_enabled: "Birthday Treat SMS Enabled (true / false — free dessert text in birthday week)",
    loyalty_reminder_sms_enabled: "Loyalty Reminder SMS Enabled (true / false — texts guests one stamp away from their reward)",
    winback_sms_enabled: "Win-Back SMS Enabled (true / false — 'we miss you' 10% offer after 30 days away)",
    daily_sales_sms_enabled: "Daily Sales Text Enabled (true / false — 10pm nightly snapshot to the owner's mobile)",
    last_shift_notes_print_at: "Last shift-notes print (ISO — read-only, for debug)"
  };

  const printTest = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/print-test`, {}, { headers: authHeaders });
      toast({ title: "Test ticket sent", description: data.message || "Check the printer." });
    } catch (e) {
      toast({ title: "Print test failed", description: errMessage(e) });
    }
  };

  return (
    <div>
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] mb-6">
        <div className="section-label mb-2 flex items-center gap-2"><ShieldCheck size={14} /> POS & Notification Settings</div>
        <p className="text-sm text-[var(--mm-cream-muted)]">
          Configure where kitchen tickets are emailed, and which numbers receive SMS alerts. Values here override the defaults in the backend env.
        </p>
      </div>

      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.key} className="flex flex-col md:flex-row md:items-center gap-3 border-b border-[var(--mm-line)]/60 pb-4">
              <div className="md:w-80 shrink-0">
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">{LABELS[r.key] || r.key}</div>
                <div className="text-[0.66rem] font-mono text-[var(--mm-cream-muted)]/60 mt-1">{r.key}</div>
              </div>
              <input
                defaultValue={r.value}
                data-testid={`setting-${r.key}`}
                onBlur={(e) => { if (e.target.value !== r.value) save(r.key, e.target.value); }}
                className="flex-1 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
              />
              {r.key === "print_webhook_url" && (
                <button
                  onClick={printTest}
                  data-testid="print-test-btn"
                  title="Send a dummy test ticket to the printer"
                  className="btn-outline inline-flex items-center gap-2 text-xs whitespace-nowrap"
                >
                  <Printer size={13} /> Print test
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ChangePasswordSection authHeaders={authHeaders} toast={toast} />
    </div>
  );
}

function ChangePasswordSection({ authHeaders, toast }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast({ title: "Passwords don't match", description: "Make sure both new-password fields are identical." });
      return;
    }
    if (next.length < 6) {
      toast({ title: "Too short", description: "New password must be at least 6 characters." });
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders },
      );
      toast({ title: "Password updated", description: data.message || "Your password has been changed." });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="change-password-section">
      <div className="section-label mb-2 flex items-center gap-2"><Lock size={13} /> Change Password</div>
      <p className="text-xs text-[var(--mm-cream-muted)] mb-4">
        Rotate the admin password. Recommended after go-live since the seeded password is documented in the codebase.
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password"
          data-testid="cp-current"
          autoComplete="current-password"
          required
          className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        />
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 6)"
          data-testid="cp-new"
          autoComplete="new-password"
          required
          minLength={6}
          className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        />
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          data-testid="cp-confirm"
          autoComplete="new-password"
          required
          minLength={6}
          className="bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none"
        />
      </div>
      <div className="flex justify-end mt-4">
        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          data-testid="cp-submit"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
          {busy ? "Updating..." : "Update Password"}
        </button>
      </div>
    </form>
  );
}

/* ----------- Exports Panel ----------- */
function ExportsPanel({ authHeaders }) {
  const token = "mamamina-taptouch-8f3b2a1c9e0d4a6b7c8e9f0a1b2c3d4e"; // will be surfaced from settings later
  const exports = [
    { key: "orders", label: "Orders", note: "All paid + pending orders with items, customer, delivery info." },
    { key: "reservations", label: "Reservations", note: "Bookings with date, time, party size." },
    { key: "customers", label: "Customers", note: "Registered users with contact + address." },
    { key: "loyalty", label: "Loyalty", note: "Stamp progress + reward-ready flag per user." },
    { key: "referrals", label: "Referrals", note: "Referral codes + shareable URLs per user." },
    { key: "catering", label: "Catering Enquiries", note: "Event enquiries from the catering form." },
    { key: "delivery", label: "Delivery Enquiries", note: "Delivery quote requests." }
  ];

  const download = async (kind) => {
    // Use fetch with auth headers, blob download so we don't lose auth in a new tab
    const res = await fetch(`${API}/exports/${kind}.csv`, { headers: authHeaders });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mamamina-${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
        <div className="section-label mb-2">TapTouch / Zapier Data Feeds</div>
        <p className="text-sm text-[var(--mm-cream-muted)]">
          Download any of these as CSV, or let TapTouch (via Zapier / Make / n8n) poll them by hitting the URL with header
          <code className="mx-2 px-2 py-0.5 bg-[var(--mm-bg)] text-[var(--mm-accent)] text-xs">X-Integration-Token: {token}</code>
          — no login required for machine-to-machine polling.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {exports.map((e) => (
          <div key={e.key} className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col justify-between gap-4">
            <div>
              <div className="font-display italic text-2xl text-[var(--mm-cream)]">{e.label}</div>
              <p className="text-xs text-[var(--mm-cream-muted)] mt-2 leading-relaxed">{e.note}</p>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] opacity-70 mt-3 font-mono break-all">
                {API}/exports/{e.key}.csv
              </div>
            </div>
            <button
              onClick={() => download(e.key)}
              data-testid={`export-${e.key}-btn`}
              className="btn-outline inline-flex items-center justify-center gap-2 self-start"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------- POS Bridge Log ----------- */
function PosLog({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/pos-log`, { headers: authHeaders });
      setItems(data.items || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const badgeFor = (s) => {
    if (s === "sent") return <span className="inline-flex items-center gap-1 text-[var(--mm-accent)] text-xs"><CheckCircle2 size={12}/> sent</span>;
    if (s === "failed") return <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle size={12}/> failed</span>;
    return <span className="inline-flex items-center gap-1 text-[var(--mm-cream-muted)] text-xs">{s}</span>;
  };

  return (
    <div>
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="section-label mb-2">POS Bridge Activity</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Every kitchen ticket emailed to TapTouch. Failures usually mean the Resend key or POS email is missing — check the Settings tab.
          </p>
        </div>
        <button onClick={load} className="btn-outline inline-flex items-center gap-2 shrink-0" data-testid="pos-log-refresh">
          <RotateCcw size={14}/> Refresh
        </button>
      </div>

      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-[var(--mm-cream-muted)] font-light">No POS tickets sent yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]">
                <th className="py-3 pr-4">When</th>
                <th className="py-3 pr-4">Kind</th>
                <th className="py-3 pr-4">Recipient</th>
                <th className="py-3 pr-4">Subject</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Ref</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={i} className="border-b border-[var(--mm-line)]/60 text-[var(--mm-cream)]">
                  <td className="py-3 pr-4">{fmtDate(r.created_at)}</td>
                  <td className="py-3 pr-4">{r.kind}</td>
                  <td className="py-3 pr-4">{r.recipient || "—"}</td>
                  <td className="py-3 pr-4">{r.subject}</td>
                  <td className="py-3 pr-4">{badgeFor(r.status)}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{(r.ref_id || "").slice(0, 14)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------- Existing DataTable for orders/reservations/etc ----------- */
function DataTable({ tab, items, onResendPos, onSetStatus, onOpenDecision, onToggleStaff, onResetPin }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[0.66rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]">
            {columnsFor(tab, { onResendPos, onSetStatus, onOpenDecision, onToggleStaff, onResetPin }).map((c) => (
              <th key={c.key} className="py-3 pr-4">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id || row.session_id} className="border-b border-[var(--mm-line)]/60 text-[var(--mm-cream)]">
              {columnsFor(tab, { onResendPos, onSetStatus, onOpenDecision, onToggleStaff, onResetPin }).map((c) => (
                <td key={c.key} className="py-3 pr-4 align-top">{c.render ? c.render(row) : row[c.key] || "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReservationActionButtons({ row, onOpenDecision }) {
  const status = (row.status || "new").toLowerCase();
  const confirmed = status === "confirmed";
  const declined = status === "declined";

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => onOpenDecision?.("confirm", row)}
        disabled={confirmed}
        data-testid={`reservation-confirm-${row.id}`}
        className={`inline-flex items-center gap-1 text-[0.6rem] tracking-[0.18em] uppercase px-2 py-1 border transition-all ${
          confirmed
            ? "bg-emerald-600 text-white border-emerald-600 cursor-default"
            : "border-emerald-600/60 text-emerald-400 hover:bg-emerald-600 hover:text-white"
        }`}
        title="Confirm and pre-assign a table"
      >
        <Check size={11} /> {confirmed
          ? (row.table_number ? `T${row.table_number}` : "Confirmed")
          : "Confirm"}
      </button>
      <button
        onClick={() => onOpenDecision?.("decline", row)}
        disabled={declined}
        data-testid={`reservation-decline-${row.id}`}
        className={`inline-flex items-center gap-1 text-[0.6rem] tracking-[0.18em] uppercase px-2 py-1 border transition-all ${
          declined
            ? "bg-red-600 text-white border-red-600 cursor-default"
            : "border-red-500/60 text-red-400 hover:bg-red-600 hover:text-white"
        }`}
        title="Decline and offer alternative times"
      >
        <XCircle size={11} /> {declined ? "Declined" : "Decline"}
      </button>
    </div>
  );
}

function KitchenStatusButtons({ row, onSetStatus }) {
  const current = row.fulfillment_status;
  const steps = [
    { key: "preparing", label: "Prepare" },
    { key: "ready", label: "Ready" },
    { key: "completed", label: "Done" }
  ];
  return (
    <div className="flex flex-col gap-1.5">
      {current && (
        <span className="text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-accent)]">
          {current.replace("_", " ")}
        </span>
      )}
      <div className="flex flex-wrap gap-1">
        {steps.map((s) => (
          <button
            key={s.key}
            onClick={() => onSetStatus?.(row.session_id, s.key)}
            data-testid={`status-${s.key}-${row.session_id}`}
            disabled={current === s.key}
            className={`text-[0.6rem] tracking-[0.18em] uppercase px-2 py-1 border transition-all ${
              current === s.key
                ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)] cursor-default"
                : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function OrdersHealthStrip({ authHeaders }) {
  const [health, setHealth] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/admin/orders/health`, { headers: authHeaders })
      .then((res) => setHealth(res.data))
      .catch(() => setHealth(null));
  }, [authHeaders]);

  if (!health) return null;
  const h24 = health.last_24h || {};
  const h7 = health.last_7d || {};
  const cells = [
    { label: "Paid (24h)", value: h24.paid || 0, tone: "text-green-500" },
    { label: "Awaiting payment (24h)", value: health.pending_now || 0, tone: "text-amber-400" },
    { label: "Expired (7d)", value: h7.expired || 0, tone: "text-[var(--mm-cream-muted)]" },
    { label: "Rescued by sweep (7d)", value: health.recovered_7d || 0, tone: "text-[var(--mm-accent)]" },
  ];
  return (
    <div className="mb-6 p-4 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid="orders-health-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cells.map((c) => (
          <div key={c.label}>
            <div className={`font-display text-3xl ${c.tone}`}>{c.value}</div>
            <div className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[var(--mm-cream-muted)] mt-3 leading-relaxed">
        A safety-net sweep checks Stripe every 3 minutes — if a guest paid but never returned to the site,
        their order is rescued automatically, the kitchen is notified, and it's counted here.
        {health.recovered_total > 0 && ` ${health.recovered_total} order${health.recovered_total === 1 ? "" : "s"} rescued all-time.`}
      </p>
    </div>
  );
}

function columnsFor(tab, ctx = {}) {
  if (tab === "orders") return [
    { key: "session_id", label: "Session", render: (r) => <span className="font-mono text-xs">{(r.session_id || "").slice(0, 18)}…</span> },
    { key: "customer_name", label: "Customer" },
    { key: "order_type", label: "Type", render: (r) => <span className="text-xs uppercase">{r.order_type || "takeaway"}{r.table_number ? ` · T#${r.table_number}` : ""}</span> },
    { key: "amount", label: "Amount", render: (r) => <Money cents={r.amount} currency={r.currency} /> },
    { key: "payment_status", label: "Pay", render: (r) => <StatusPill s={r.payment_status} /> },
    { key: "items", label: "Items", render: (r) => (r.items || []).map((i) => `${(i.lookup_key || "").replace("mamamina_", "")}×${i.quantity}`).join(", ") },
    { key: "pos_ticket_sent", label: "POS", render: (r) => (
      <div className="flex items-center gap-2">
        {r.pos_ticket_sent ? <CheckCircle2 size={14} className="text-[var(--mm-accent)]"/> : <XCircle size={14} className="text-[var(--mm-cream-muted)]"/>}
        <button
          onClick={() => ctx.onResendPos?.(r.session_id)}
          className="text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)]"
          data-testid={`resend-pos-${r.session_id}`}
        >Resend</button>
      </div>
    )},
    { key: "kitchen", label: "Kitchen", render: (r) => (
      <KitchenStatusButtons row={r} onSetStatus={ctx.onSetStatus} />
    )},
    { key: "created_at", label: "Placed", render: (r) => fmtDate(r.created_at) }
  ];
  if (tab === "reservations") return [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "date", label: "Date" },
    { key: "time", label: "Time" },
    { key: "party_size", label: "Party" },
    { key: "notes", label: "Notes" },
    { key: "status", label: "Status", render: (r) => <StatusPill s={r.status} /> },
    { key: "actions", label: "Actions", render: (r) => (
      <ReservationActionButtons row={r} onOpenDecision={ctx.onOpenDecision} />
    )},
    { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) }
  ];
  if (tab === "catering") return [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "event_type", label: "Event" },
    { key: "event_date", label: "Date" },
    { key: "guests", label: "Guests" },
    { key: "message", label: "Message" },
    { key: "status", label: "Status", render: (r) => <StatusPill s={r.status} /> },
    { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) }
  ];
  if (tab === "delivery") return [
    { key: "name", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "address", label: "Address" },
    { key: "postcode", label: "Postcode" },
    { key: "zone", label: "Zone" },
    { key: "fee", label: "Fee", render: (r) => r.fee ? `$${r.fee}` : "—" },
    { key: "cart_total", label: "Cart", render: (r) => r.cart_total ? `$${r.cart_total.toFixed(2)}` : "—" },
    { key: "notes", label: "Notes" },
    { key: "status", label: "Status", render: (r) => <StatusPill s={r.status} /> },
    { key: "created_at", label: "Received", render: (r) => fmtDate(r.created_at) }
  ];
  if (tab === "users") return [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "is_admin", label: "Admin", render: (r) => r.is_admin ? "Yes" : "" },
    {
      key: "is_staff",
      label: "Staff",
      render: (r) => (
        r.is_admin ? (
          <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">n/a</span>
        ) : (
          <label className="inline-flex items-center cursor-pointer" title="Toggle back-office access">
            <input
              type="checkbox"
              checked={!!r.is_staff}
              onChange={() => ctx.onToggleStaff?.(r)}
              className="sr-only peer"
              data-testid={`user-staff-toggle-${r.id}`}
            />
            <span className={`w-10 h-5 rounded-full transition-colors relative ${r.is_staff ? "bg-[var(--mm-accent)]" : "bg-[var(--mm-line)]"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${r.is_staff ? "translate-x-5" : ""}`} />
            </span>
          </label>
        )
      ),
    },
    {
      key: "pin",
      label: "PIN",
      render: (r) => {
        if (r.is_admin) return <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">n/a</span>;
        if (!r.is_staff) return <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]/50">—</span>;
        return (
          <div className="flex items-center gap-2">
            {r.pin_set ? (
              <span
                className={`text-[0.6rem] tracking-[0.22em] uppercase px-2 py-0.5 border ${
                  r.pin_locked
                    ? "border-red-400 text-red-400 bg-red-400/10"
                    : "border-[var(--mm-accent)] text-[var(--mm-accent)]"
                }`}
                data-testid={`user-pin-status-${r.id}`}
              >
                {r.pin_locked ? "Locked" : "Set"}
              </span>
            ) : (
              <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]/50">
                Not set
              </span>
            )}
            {(r.pin_set || r.pin_locked) && (
              <button
                onClick={() => ctx.onResetPin?.(r)}
                data-testid={`user-pin-reset-${r.id}`}
                title={r.pin_locked ? "Unlock this account and clear its PIN" : "Clear this user's PIN"}
                className="text-[0.6rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors underline decoration-dotted underline-offset-4"
              >
                {r.pin_locked ? "Unlock" : "Reset"}
              </button>
            )}
          </div>
        );
      },
    },
    { key: "created_at", label: "Joined", render: (r) => fmtDate(r.created_at) }
  ];
  return [];
}

function StatusPill({ s }) {
  if (!s) return <>—</>;
  const cls =
    s === "paid" || s === "completed" || s === "confirmed"
      ? "border-emerald-500 text-emerald-400"
      : s === "declined" || s === "cancelled" || s === "no_show"
      ? "border-red-500 text-red-400"
      : s === "new"
      ? "border-[var(--mm-cream)] text-[var(--mm-cream)]"
      : "border-[var(--mm-cream-muted)] text-[var(--mm-cream-muted)]";
  return <span className={`inline-block px-2 py-0.5 border text-[0.65rem] tracking-[0.2em] uppercase ${cls}`}>{s}</span>;
}
